import './SliderInputNumber.scss';

import View                      from '@widesk-ui/components/View';
import Slider                    from '@widesk-ui/components/form/Slider';
import { SliderProps }           from '@widesk-ui/components/form/Slider';
import React                     from 'react';
import ReactDOM                  from 'react-dom';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { FieldLabelSlotContext } from '@/TokamapEditor/ux/AreaSidebar/Field';

const INCREASE_KEYS = ['ArrowRight', 'ArrowUp'];
const DECREASE_KEYS = ['ArrowLeft', 'ArrowDown'];

const roundToStep = (value: number | undefined, step: number | null | undefined) => {
	if (value === undefined || value === null || Number.isNaN(value)) return value;

	const decimals = Math.min(4, (String(step ?? 0.01).split('.')[1] ?? '').length);

	return Number(value.toFixed(decimals));
};

const SliderInputNumber = (props: Omit<SliderProps, 'onChange' | 'value' | 'onAfterChange' | 'range'> & {
	value?: number;
	onChange?: (value?: number) => void;
	onAfterChange?: (value?: number) => void;
	range?: false;
	suffix?: React.ReactNode;
	loading?: boolean;
	inputMax?: number;
	resetValue?: number;
	unitSelect?: {
		value: string;
		options: { value: string; label: string; }[];
		onChange: (value: string) => void;
		title?: string;
	};
}) => {
	const { inputMax, resetValue, suffix, loading, unitSelect, ...sliderProps } = props;
	const tokens = useDesignTokens();
	const slot = React.useContext(FieldLabelSlotContext);
	const [draft, setDraft] = React.useState<string>();
	// Valeur réglée au clavier pas encore enregistrée : la forme n'est relue qu'après coup, et chaque
	// appui doit partir de la précédente plutôt que de l'ancienne valeur de la forme.
	const [pending, setPending] = React.useState<number>();
	// Arrondie au pas : une valeur stockée en fraction (0.56) puis remise en pourcentage donnerait
	// sinon 56.00000000000001.
	const value = roundToStep(pending ?? props.value, props.step);
	const lastValue = React.useRef(value);
	const commitTimeout = React.useRef<ReturnType<typeof setTimeout>>();

	lastValue.current = value;

	React.useEffect(() => () => clearTimeout(commitTimeout.current), []);

	const min = props.min ?? 0;
	const max = inputMax ?? props.max ?? 100;

	const clamp = (value: number) => Math.min(max, Math.max(min, value));

	const change = (value: number) => {
		lastValue.current = value;
		props.onChange?.(value);
	};

	const commitLater = (value: number) => {
		clearTimeout(commitTimeout.current);
		commitTimeout.current = setTimeout(() => {
			setPending(undefined);
			props.onAfterChange?.(value);
		}, 400);
	};

	const nudge = (direction: 1 | -1, large: boolean) => {
		const step = props.step ?? 1;
		const base = lastValue.current ?? min;
		const value = clamp(Math.round((base + direction * step * (large ? 10 : 1)) / step) * step);

		setDraft(undefined);
		setPending(value);
		change(value);
		commitLater(value);
	};

	const jump = (value: number) => {
		setDraft(undefined);
		setPending(value);
		change(value);
		commitLater(value);
	};

	const commit = (raw: string | undefined) => {
		setDraft(undefined);

		const parsed = Number(String(raw ?? '').replace(',', '.'));

		if (raw === undefined || raw === '' || Number.isNaN(parsed)) return;

		const value = Math.min(max, Math.max(min, parsed));

		props.onChange?.(value);
		props.onAfterChange?.(value);
	};

	const valueField = (
		<label
			className="slider-input-number__value"
			style={{
				'--slider-value-hover-bg': tokens.surface.subtle,
				'--slider-value-focus-bg': tokens.surface.elevated,
				'--slider-value-focus-border': tokens.color.primary,
				opacity: loading ? 0.5 : 1,
			} as React.CSSProperties}
		>
			<input
				type="number"
				inputMode="decimal"
				min={min}
				max={max}
				step={props.step ?? undefined}
				disabled={props.disabled}
				value={draft ?? (value === undefined ? '' : String(value))}
				onChange={e => {
					setDraft(e.target.value);

					const parsed = Number(e.target.value);

					if (e.target.value !== '' && !Number.isNaN(parsed)) props.onChange?.(Math.min(max, Math.max(min, parsed)));
				}}
				onBlur={e => commit(e.target.value)}
				onKeyDown={e => {
					if (e.key === 'Enter') e.currentTarget.blur();

					if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
						e.preventDefault();
						nudge(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey);
					}
				}}
			/>

			{unitSelect ? (
				<select
					aria-label="Unité"
					title={unitSelect.title}
					value={unitSelect.value}
					onChange={e => unitSelect.onChange(e.target.value)}
					style={{
						border: 0,
						padding: '0 2px',
						background: 'transparent',
						font: 'inherit',
						fontSize: 12,
						color: tokens.text.muted,
						cursor: 'pointer',
					}}
				>
					{unitSelect.options.map(option => (
						<option key={option.value} value={option.value}>{option.label}</option>
					))}
				</select>
			) : !!suffix && <span style={{ fontSize: 12, color: tokens.text.muted }}>{suffix}</span>}
		</label>
	);

	const resetPercent = resetValue === undefined || props.max === undefined
		? undefined
		: ((resetValue - min) / ((props.max - min) || 1)) * 100;

	return (
		<View gap={2}>
			{slot ? ReactDOM.createPortal(valueField, slot) : (
				<View row style={{ justifyContent: 'flex-end' }}>{valueField}</View>
			)}

			<div
				className="slider-input-number__track"
				onKeyDownCapture={e => {
					if (props.disabled) return;

					const sliderMax = props.max ?? max;
					const handled = INCREASE_KEYS.includes(e.key) || DECREASE_KEYS.includes(e.key)
						|| e.key === 'Home' || e.key === 'End';

					if (!handled) return;

					e.preventDefault();
					e.stopPropagation();

					if (e.key === 'Home') jump(min);
					else if (e.key === 'End') jump(Math.max(sliderMax, lastValue.current ?? min));
					else nudge(INCREASE_KEYS.includes(e.key) ? 1 : -1, e.shiftKey);
				}}
				onDoubleClick={() => {
					if (resetValue === undefined) return;

					props.onChange?.(resetValue);
					props.onAfterChange?.(resetValue);
				}}
				title={resetValue === undefined ? undefined : 'Flèches du clavier pour ajuster (Maj : plus vite). Double-clic : revenir à la valeur par défaut'}
			>
				<Slider
					style={{ margin: '6px 0 10px' }}
					disabled={props.disabled}
					{...sliderProps}
					value={value}
					onChange={(value: number) => change(value)}
				/>

				{resetPercent !== undefined && (
					<span
						className="slider-input-number__default"
						style={{ left: `calc(100% * ${resetPercent / 100} - 1px)`, background: tokens.border.default }}
					/>
				)}
			</div>
		</View>
	);
};

export default SliderInputNumber;
